import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { CheckCircle2, AlertCircle, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { MetaAdsOAuthSection } from './MetaAdsOAuthSection';
import { MetaAdsV1ConfigSection } from './MetaAdsV1ConfigSection';
import { MetaAdsAccountSelector } from './MetaAdsAccountSelector';
import { MetaAdsCampaignsSection } from './MetaAdsCampaignsSection';

console.log('🚀 META PHASE3 LOADED - MetaAdsIntegration component imported');

export function MetaAdsIntegration() {
  console.log('📝 MetaAdsIntegration function called');
  const { authAxios } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [oauthConnected, setOauthConnected] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [currency, setCurrency] = useState('EUR');

  const [config, setConfig] = useState({
    ad_account_id: '',
    pixel_id: '',
    campaign_id: '',
    campaign_url: '',
  });

  // Load config on mount
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        // Check OAuth status
        try {
          const oauthRes = await authAxios.get('/marketing/meta-ads/oauth/status');
          setOauthConnected(oauthRes.data.connected);
        } catch (err) {
          if (err.response?.status !== 404) {
            console.warn('OAuth status check failed:', err);
          }
        }

        // Check v1 config
        try {
          const res = await authAxios.get('/marketing/meta-ads/config');
          setConfig({
            ad_account_id: res.data.ad_account_id || '',
            pixel_id: res.data.pixel_id || '',
            campaign_id: res.data.campaign_id || '',
            campaign_url: res.data.campaign_url || '',
          });

          // If account is configured, set selectedAccount + currency
          if (res.data.ad_account_id) {
            const accountCurrency = res.data.currency || 'EUR';
            setCurrency(accountCurrency);
            setSelectedAccount({
              id: res.data.ad_account_id,
              name: res.data.ad_account_name || 'Conta selecionada',
              currency: accountCurrency,
            });
          }
        } catch (err) {
          if (err.response?.status !== 404) {
            toast.error('Erro ao carregar configuração Meta Ads');
          }
        }
      } finally {
        setLoading(false);
      }
    };

    fetchStatus();
  }, [authAxios]);

  const handleSaveV1 = async (newConfig) => {
    setSaving(true);
    try {
      await authAxios.post('/marketing/meta-ads/config', {
        ad_account_id: newConfig.ad_account_id.trim(),
        pixel_id: newConfig.pixel_id.trim() || null,
        campaign_id: newConfig.campaign_id.trim() || null,
        campaign_url: newConfig.campaign_url.trim() || null,
      });
      setConfig(newConfig);
      toast.success('Configuração v1 salva com sucesso');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || 'Erro ao salvar configuração');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40 text-muted-foreground text-sm">
        <div className="animate-pulse">Carregando configuração Meta Ads...</div>
      </div>
    );
  }

  const connectionConfig = oauthConnected
    ? { color: 'text-green-600', bg: 'bg-green-50', label: 'Conectado' }
    : { color: 'text-gray-500', bg: 'bg-gray-50', label: 'Desconectado' };

  // DEBUG
  console.log('[MetaAdsIntegration]', {
    oauthConnected,
    selectedAccount,
    config_ad_account_id: config.ad_account_id,
  });

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-3 bg-slate-50 border-b">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="bg-blue-500/10 text-blue-600 p-2 rounded-md">
              <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </span>
            <div>
              <CardTitle className="text-base">Meta Ads Integration</CardTitle>
              <CardDescription className="text-xs">Conexão OAuth + Configuração manual (v1/v2)</CardDescription>
            </div>
          </div>
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-md ${connectionConfig.bg}`}>
            {oauthConnected ? (
              <CheckCircle2 className={`w-4 h-4 ${connectionConfig.color}`} />
            ) : (
              <AlertCircle className={`w-4 h-4 ${connectionConfig.color}`} />
            )}
            <span className={`text-xs font-semibold ${connectionConfig.color}`}>{connectionConfig.label}</span>
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-6 space-y-6">
        {/* Step 1: OAuth connection */}
        <MetaAdsOAuthSection />

        {/* Step 2: Account selection (only if OAuth is connected) */}
        {oauthConnected && (
          <MetaAdsAccountSelector
            oauthConnected={oauthConnected}
            onAccountSelected={(account) => {
              setSelectedAccount(account);
              setConfig(prev => ({
                ...prev,
                ad_account_id: account.id,
                ad_account_name: account.name,
              }));
            }}
          />
        )}

        {/* Step 3: Campaigns (only if account is selected) */}
        {selectedAccount && (
          <MetaAdsCampaignsSection
            oauthConnected={oauthConnected}
            configuredAccountId={selectedAccount.id}
            currency={currency}
          />
        )}

        {/* Step 4: Optional v1 config (always available) */}
        <MetaAdsV1ConfigSection initialConfig={config} onSave={handleSaveV1} saving={saving} />
      </CardContent>
    </Card>
  );
}
